import React, { useRef, useState, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import {
  CELL_TYPES,
  sortCellsByZIndex,
  findNonOverlappingPosition,
} from './cellSchema';

const RESIZE_HANDLES = [
  { position: 'nw', cursor: 'nwse-resize' },
  { position: 'n', cursor: 'ns-resize' },
  { position: 'ne', cursor: 'nesw-resize' },
  { position: 'e', cursor: 'ew-resize' },
  { position: 'se', cursor: 'nwse-resize' },
  { position: 's', cursor: 'ns-resize' },
  { position: 'sw', cursor: 'nesw-resize' },
  { position: 'w', cursor: 'ew-resize' },
];

/* ── Compute minimum column/row pixel sizes based on components inside a table ── */
const getTableMinDims = (tableCell, allCells, PAD = 8) => {
  const tm = tableCell.tableModel;
  if (!tm) return { minColPx: [], minRowPx: [], minTableW: 80, minTableH: 40 };
  const nR = tm.grid.length, nC = tm.grid[0]?.length || 1;
  const cwNorm = (tm.colWidths || []).map((v) => (v != null ? v : 100 / nC));
  const rhNorm = (tm.rowHeights || []).map((v) => (v != null ? v : 100 / nR));
  const cPx = [0]; for (let i = 0; i < nC; i++) cPx.push(cPx[i] + (cwNorm[i] / 100) * tableCell.width);
  const rPx = [0]; for (let i = 0; i < nR; i++) rPx.push(rPx[i] + (rhNorm[i] / 100) * tableCell.height);
  const minColPx = new Array(nC).fill(20);
  const minRowPx = new Array(nR).fill(20);
  let minTableW = 80, minTableH = 40;
  for (const comp of allCells) {
    if (comp.id === tableCell.id || comp.type === CELL_TYPES.TABLE) continue;
    const cx = comp.x + comp.width / 2, cy = comp.y + comp.height / 2;
    if (cx < tableCell.x || cx > tableCell.x + tableCell.width ||
        cy < tableCell.y || cy > tableCell.y + tableCell.height) continue;
    const relCx = cx - tableCell.x, relCy = cy - tableCell.y;
    let ci = nC - 1; for (let k = 0; k < nC; k++) { if (relCx < cPx[k + 1]) { ci = k; break; } }
    let ri = nR - 1; for (let k = 0; k < nR; k++) { if (relCy < rPx[k + 1]) { ri = k; break; } }
    const neededColPx = comp.width + 2 * PAD;
    const neededRowPx = comp.height + 2 * PAD;
    minColPx[ci] = Math.max(minColPx[ci], neededColPx);
    minRowPx[ri] = Math.max(minRowPx[ri], neededRowPx);
    // A column at cwNorm[ci]% of the table must hold neededColPx pixels
    // → minTableW = neededColPx / (cwNorm[ci] / 100)
    if (cwNorm[ci] > 0) minTableW = Math.max(minTableW, neededColPx * 100 / cwNorm[ci]);
    if (rhNorm[ri] > 0) minTableH = Math.max(minTableH, neededRowPx * 100 / rhNorm[ri]);
  }
  // Also enforce: table must be tall/wide enough for the SUM of all row/col minimums
  // (prevents overlap when components are in different rows of the same column)
  const sumColMin = minColPx.reduce((a, b) => a + b, 0);
  const sumRowMin = minRowPx.reduce((a, b) => a + b, 0);
  minTableW = Math.max(minTableW, sumColMin);
  minTableH = Math.max(minTableH, sumRowMin);
  return {
    minColPx,
    minRowPx,
    minTableW: Math.round(minTableW),
    minTableH: Math.round(minTableH),
  };
};

const RenderedCell = ({
  cell,
  selectedCellId,
  editingCellId,
  draggingCellId,
  resizingCellId,
  onSelectCell,
  onUpdateCell,
  onUpdateCellComplete,
  onContentChange,
  onImageUrlChange,
  onCellInput,
  onLoopVarTrigger,
  isReadOnly,
  onDragStart,
  onResizeStart,
  onDoubleClick,
  tableCellHighlight,
  allCells,
}) => {
  const isSelected = cell.id === selectedCellId;
  const isEditing = cell.id === editingCellId;
  const isDragging = draggingCellId === cell.id;
  const isResizingThis = resizingCellId === cell.id;
  const isComponentCell = [CELL_TYPES.BUTTON, CELL_TYPES.DIVIDER, CELL_TYPES.SOCIAL].includes(
    cell.type,
  );
  const isTableCell = cell.type === CELL_TYPES.TABLE;

  const handleCellDoubleClick = (e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    if (cell.type === CELL_TYPES.TEXT) {
    }
  };

  return (
    <div
      key={cell.id}
      className={`cell-item${isSelected ? ' selected' : ''}${isDragging ? ' dragging' : ''}${
        isResizingThis ? ' resizing' : ''
      }${isEditing ? ' editing' : ''}`}
      style={{
        position: 'absolute',
        left: `${cell.x}px`,
        top: `${cell.y}px`,
        width: `${cell.width}px`,
        height: `${cell.height}px`,
        zIndex: cell.zIndex || 1,
        backgroundColor: (isComponentCell || isTableCell)
          ? 'transparent'
          : cell.styles?.backgroundColor || 'transparent',
        padding: (isComponentCell || isTableCell) ? '0' : cell.styles?.padding || '0',
        borderRadius: (isComponentCell || isTableCell) ? '0' : cell.styles?.borderRadius || '0',
        border: isTableCell ? 'none' : (isSelected ? '2px solid #29e67c' : '1px dashed transparent'),
        outline: isTableCell && isSelected ? '2px solid #29e67c' : 'none',
        outlineOffset: isTableCell && isSelected ? '0px' : undefined,
        cursor: isEditing ? 'text' : 'move',
        boxSizing: 'border-box',
        overflow: 'visible',
        pointerEvents: 'auto',
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!isReadOnly) onSelectCell(cell.id);
      }}
      onDoubleClick={(e) => {
        if (onDoubleClick) onDoubleClick(e, cell);
      }}
      onMouseDown={(e) => !isEditing && onDragStart && onDragStart(e, cell)}
      data-cell-id={cell.id}
      data-cell-type={cell.type}
    >
      <CellContent
        cell={cell}
        isEditing={isEditing}
        isSelected={isSelected}
        onContentChange={(content) => onContentChange(cell.id, content)}
        onImageUrlChange={(url) => onImageUrlChange(cell.id, url)}
        onCellInput={onCellInput}
        onLoopVarTrigger={onLoopVarTrigger}
        onSelectCell={onSelectCell}
        onUpdateCell={onUpdateCell}
        onUpdateCellComplete={onUpdateCellComplete}
        selectedCellId={selectedCellId}
        isReadOnly={isReadOnly}
        tableCellHighlight={tableCellHighlight}
        allCells={allCells}
      />

      {isSelected && !isEditing && !isReadOnly && (
        <>
          {RESIZE_HANDLES.map((handle) => (
            <div
              key={handle.position}
              className={`cell-resize-handle handle-${handle.position}`}
              style={{ cursor: handle.cursor }}
              onMouseDown={(e) => onResizeStart && onResizeStart(e, cell, handle)}
            />
          ))}
        </>
      )}

      {/* {isSelected && !isEditing && !isReadOnly && (
        <div
          className={`cell-overlap-toggle ${cell.allowOverlap ? 'active' : ''}`}
          title={
            cell.allowOverlap
              ? 'Overlap allowed - Click to disable'
              : 'Overlap disabled - Click to allow'
          }
          onClick={(e) => {
            e.stopPropagation();
            const newValue = !cell.allowOverlap;
            onUpdateCell(cell.id, { allowOverlap: newValue });
            if (onUpdateCellComplete) {
              onUpdateCellComplete(cell.id, { allowOverlap: newValue });
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <FlipToFrontIcon style={{ fontSize: 14 }} />
        </div>
      )} */}

      {isSelected && !isEditing && <div className='cell-type-badge'>{cell.type}</div>}
    </div>
  );
};

/* ── Helpers for rendering components inside table cells ── */
const renderTableCellComponent = (comp) => {
  if (!comp) return null;
  const { type, props = {} } = comp;
  switch (type) {
    case 'text':
      return <div style={{ fontSize: 14 }}>{props.text || 'Text'}</div>;
    case 'button':
      return (
        <div style={{ textAlign: props.align || 'center' }}>
          <span style={{
            display: 'inline-block', padding: '8px 16px',
            backgroundColor: props.bg || '#7a6a55', color: props.color || '#ffffff',
            borderRadius: props.radius || 4, fontSize: 13, cursor: 'pointer',
          }}>{props.text || 'Button'}</span>
        </div>
      );
    case 'image':
      return props.src
        ? <img src={props.src} alt={props.alt || ''} style={{ maxWidth: '100%', display: 'block' }} />
        : <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: 8 }}>[Image]</div>;
    case 'divider':
      return <hr style={{ border: 'none', borderTop: `${props.thickness || 1}px solid ${props.color || '#ccc'}`, margin: '4px 0' }} />;
    case 'social':
      return <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: 4 }}>[Social]</div>;
    default:
      return <div style={{ color: '#999', fontSize: 12 }}>[{type}]</div>;
  }
};

/* ── Table renderer with cell-edit toggle & resize handles ── */
const HANDLE_SIZE = 8;
const HANDLE_COLOR = '#29e67c';

const TableCellRenderer = ({ cell, onUpdateCell, onUpdateCellComplete, tableCellHighlight, allCells }) => {
  const containerRef = useRef(null);
  const [cellEditMode, setCellEditMode] = useState(false);
  const [selectedTC, setSelectedTC] = useState(null);
  const updateCompleteRef = useRef(onUpdateCellComplete);
  updateCompleteRef.current = onUpdateCellComplete;
  const updateRef = useRef(onUpdateCell);
  updateRef.current = onUpdateCell;

  const tm = cell.tableModel;
  if (!tm) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13, height: '100%' }}>
        Table (no data)
      </div>
    );
  }

  const bw = tm.tableBorderWidth != null ? tm.tableBorderWidth : 1;
  const bc = tm.tableBorderColor || '#000000';
  const bs = tm.tableBorderStyle || 'solid';
  const numCols = tm.grid[0]?.length || 1;
  const numRows = tm.grid.length;

  const cw = [...(tm.colWidths || [])];
  while (cw.length < numCols) cw.push(100 / numCols);

  const rowPercents = (tm.rowHeights || []).map((h) => (h != null ? h : 100 / numRows));
  while (rowPercents.length < numRows) rowPercents.push(100 / numRows);

  const colTemplate = cw.map((w) => `${Math.max(0.1, w)}fr`).join(' ');
  const rowTemplate = rowPercents.map((h) => `${Math.max(0.1, h)}fr`).join(' ');

  /* ── Internal edge resize (redistribute % between two adjacent cols/rows) ── */
  const startEdgeResize = (e, type, idx) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    const isCol = type === 'col';
    const startPos = isCol ? e.clientX : e.clientY;
    const containerSize = isCol ? rect.width : rect.height;
    const startSizes = isCol ? [...cw] : [...rowPercents];

    // Compute component-based minimum percentages for the two affected cols/rows
    const dims = getTableMinDims(cell, allCells || []);
    const minArr = isCol ? dims.minColPx : dims.minRowPx;
    const totalPx = isCol ? cell.width : cell.height;
    const minPctA = totalPx > 0 ? (minArr[idx] / totalPx) * 100 : 5;
    const minPctB = totalPx > 0 ? (minArr[idx + 1] / totalPx) * 100 : 5;

    const clampSizes = (dp) => {
      const ns = [...startSizes];
      ns[idx] = Math.max(minPctA, startSizes[idx] + dp);
      ns[idx + 1] = Math.max(minPctB, startSizes[idx + 1] - dp);
      // If clamping one side pushed the other below its min, re-clamp
      const total = startSizes[idx] + startSizes[idx + 1];
      if (ns[idx] + ns[idx + 1] > total + 0.01) {
        if (dp > 0) ns[idx] = total - ns[idx + 1];
        else ns[idx + 1] = total - ns[idx];
      }
      return ns;
    };

    const onMM = (ev) => {
      const delta = (isCol ? ev.clientX : ev.clientY) - startPos;
      const dp = (delta / containerSize) * 100;
      const ns = clampSizes(dp);
      const nm = JSON.parse(JSON.stringify(cell.tableModel));
      if (isCol) nm.colWidths = ns; else nm.rowHeights = ns;
      updateRef.current(cell.id, { tableModel: nm });
    };
    const onMU = (ev) => {
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('mouseup', onMU);
      const delta = (isCol ? ev.clientX : ev.clientY) - startPos;
      const dp = (delta / containerSize) * 100;
      const fs = clampSizes(dp);
      const fm = JSON.parse(JSON.stringify(cell.tableModel));
      if (isCol) fm.colWidths = fs; else fm.rowHeights = fs;
      if (updateCompleteRef.current) updateCompleteRef.current(cell.id, { tableModel: fm });
    };
    window.addEventListener('mousemove', onMM);
    window.addEventListener('mouseup', onMU);
  };

  /* ── Outer edge resize (changes canvas cell width / height / x / y) ── */
  const startOuterResize = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = cell.width;
    const startH = cell.height;
    const startCX = cell.x;
    const startCY = cell.y;
    const dims = getTableMinDims(cell, allCells || []);
    const minW = dims.minTableW;
    const minH = dims.minTableH;

    const calc = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const u = {};
      if (direction.includes('right')) u.width = Math.max(minW, startW + dx);
      if (direction.includes('bottom')) u.height = Math.max(minH, startH + dy);
      if (direction.includes('left')) { u.width = Math.max(minW, startW - dx); u.x = startCX + (startW - u.width); }
      if (direction.includes('top')) { u.height = Math.max(minH, startH - dy); u.y = startCY + (startH - u.height); }
      return u;
    };
    const onMM = (ev) => updateRef.current(cell.id, calc(ev));
    const onMU = (ev) => {
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('mouseup', onMU);
      if (updateCompleteRef.current) updateCompleteRef.current(cell.id, calc(ev));
    };
    window.addEventListener('mousemove', onMM);
    window.addEventListener('mouseup', onMU);
  };

  /* ── Build resize handles for a selected cell ── */
  const renderHandles = (ri, ci) => {
    const handles = [];
    const hs = HANDLE_SIZE;
    const hh = hs / 2;
    const base = { position: 'absolute', width: hs, height: hs, backgroundColor: HANDLE_COLOR, zIndex: 20, borderRadius: 1 };

    const isTop = ri === 0;
    const isBottom = ri === numRows - 1;
    const isLeft = ci === 0;
    const isRight = ci === numCols - 1;

    // Right-center handle
    if (!isRight) {
      handles.push(<div key='r' style={{ ...base, right: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'col-resize' }}
        onMouseDown={(e) => startEdgeResize(e, 'col', ci)} />);
    } else {
      handles.push(<div key='r' style={{ ...base, right: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'e-resize' }}
        onMouseDown={(e) => startOuterResize(e, 'right')} />);
    }

    // Left-center handle
    if (!isLeft) {
      handles.push(<div key='l' style={{ ...base, left: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'col-resize' }}
        onMouseDown={(e) => startEdgeResize(e, 'col', ci - 1)} />);
    } else {
      handles.push(<div key='l' style={{ ...base, left: -hh, top: '50%', transform: 'translateY(-50%)', cursor: 'w-resize' }}
        onMouseDown={(e) => startOuterResize(e, 'left')} />);
    }

    // Bottom-center handle
    if (!isBottom) {
      handles.push(<div key='b' style={{ ...base, bottom: -hh, left: '50%', transform: 'translateX(-50%)', cursor: 'row-resize' }}
        onMouseDown={(e) => startEdgeResize(e, 'row', ri)} />);
    } else {
      handles.push(<div key='b' style={{ ...base, bottom: -hh, left: '50%', transform: 'translateX(-50%)', cursor: 's-resize' }}
        onMouseDown={(e) => startOuterResize(e, 'bottom')} />);
    }

    // Top-center handle
    if (!isTop) {
      handles.push(<div key='t' style={{ ...base, top: -hh, left: '50%', transform: 'translateX(-50%)', cursor: 'row-resize' }}
        onMouseDown={(e) => startEdgeResize(e, 'row', ri - 1)} />);
    } else {
      handles.push(<div key='t' style={{ ...base, top: -hh, left: '50%', transform: 'translateX(-50%)', cursor: 'n-resize' }}
        onMouseDown={(e) => startOuterResize(e, 'top')} />);
    }

    // Corner: top-left
    const tlDir = (isTop ? 'top' : '') + (isLeft ? 'left' : '');
    handles.push(<div key='tl' style={{ ...base, top: -hh, left: -hh, cursor: 'nw-resize' }}
      onMouseDown={tlDir ? (e) => startOuterResize(e, tlDir || 'top-left') : undefined} />);

    // Corner: top-right
    const trDir = (isTop ? 'top' : '') + (isRight ? 'right' : '');
    handles.push(<div key='tr' style={{ ...base, top: -hh, right: -hh, cursor: 'ne-resize' }}
      onMouseDown={trDir ? (e) => startOuterResize(e, trDir || 'top-right') : undefined} />);

    // Corner: bottom-left
    const blDir = (isBottom ? 'bottom' : '') + (isLeft ? 'left' : '');
    handles.push(<div key='bl' style={{ ...base, bottom: -hh, left: -hh, cursor: 'sw-resize' }}
      onMouseDown={blDir ? (e) => startOuterResize(e, blDir || 'bottom-left') : undefined} />);

    // Corner: bottom-right
    const brDir = (isBottom ? 'bottom' : '') + (isRight ? 'right' : '');
    if (brDir) {
      handles.push(<div key='br' style={{ ...base, bottom: -hh, right: -hh, cursor: 'se-resize' }}
        onMouseDown={(e) => startOuterResize(e, brDir)} />);
    } else if (!isRight && !isBottom) {
      handles.push(<div key='br' style={{ ...base, bottom: -hh, right: -hh, cursor: 'nwse-resize' }}
        onMouseDown={(e) => { startEdgeResize(e, 'col', ci); startEdgeResize(e, 'row', ri); }} />);
    } else {
      handles.push(<div key='br' style={{ ...base, bottom: -hh, right: -hh, cursor: 'default' }} />);
    }

    return handles;
  };

  const toggleCellEdit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const next = !cellEditMode;
    setCellEditMode(next);
    if (!next) setSelectedTC(null);
  };

  return (
    <div
      ref={containerRef}
      className='cell-table-content'
      style={{ width: '100%', height: '100%', position: 'relative', pointerEvents: 'none' }}
      onClick={() => { if (cellEditMode) setSelectedTC(null); }}
    >
      {/* Cell-edit toggle button — keeps pointer-events so it stays clickable */}
      <div
        onClick={toggleCellEdit}
        title={cellEditMode ? 'Exit cell editing' : 'Edit individual cells'}
        style={{
          position: 'absolute', top: -14, right: -14, zIndex: 30,
          width: 24, height: 24, borderRadius: 4,
          backgroundColor: cellEditMode ? HANDLE_COLOR : '#fff',
          border: `2px solid ${cellEditMode ? HANDLE_COLOR : '#999'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
          pointerEvents: 'auto',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="1" width="5" height="5" stroke={cellEditMode ? '#fff' : '#666'} strokeWidth="1.2" fill="none" />
          <rect x="8" y="1" width="5" height="5" stroke={cellEditMode ? '#fff' : '#666'} strokeWidth="1.2" fill="none" />
          <rect x="1" y="8" width="5" height="5" stroke={cellEditMode ? '#fff' : '#666'} strokeWidth="1.2" fill="none" />
          <rect x="8" y="8" width="5" height="5" stroke={cellEditMode ? '#fff' : '#666'} strokeWidth="1.2" fill="none" />
        </svg>
      </div>

      {/* Grid — border-only, transparent cells, pointer-events: none so canvas components behind show through */}
      <div
        style={{
          display: 'grid', gridTemplateColumns: colTemplate, gridTemplateRows: rowTemplate,
          width: '100%', height: '100%',
        }}
      >
        {tm.grid.map((row, ri) =>
          row.map((tCell, ci) => {
            if (tCell._merged) return null;
            const isSel = cellEditMode && selectedTC && selectedTC.ri === ri && selectedTC.ci === ci;
            const isHighlighted = tableCellHighlight && tableCellHighlight.tableCellId === cell.id && tableCellHighlight.ri === ri && tableCellHighlight.ci === ci;
            const spanStyle = {};
            if (tCell.colspan > 1) spanStyle.gridColumn = `span ${tCell.colspan}`;
            if (tCell.rowspan > 1) spanStyle.gridRow = `span ${tCell.rowspan}`;
            return (
              <div
                key={`${ri}-${ci}`}
                style={{
                  border: (() => {
                    const cbs = tCell.borderStyle != null ? tCell.borderStyle : bs;
                    if (cbs === 'none') return 'none';
                    const cbw = tCell.borderWidth != null ? tCell.borderWidth : bw;
                    const cbc = tCell.borderColor != null ? tCell.borderColor : bc;
                    return `${cbw}px ${cbs} ${cbc}`;
                  })(),
                  backgroundColor: isHighlighted ? 'rgba(41,230,124,0.25)' : (tCell.bgColor && tCell.bgColor !== '#ffffff' ? tCell.bgColor : 'transparent'),
                  padding: `${tCell.padding || 8}px`,
                  textAlign: tCell.align || 'left',
                  wordWrap: 'break-word', overflowWrap: 'break-word',
                  minHeight: 24, boxSizing: 'border-box',
                  position: 'relative',
                  outline: isSel ? `2px solid ${HANDLE_COLOR}` : (isHighlighted ? `2px solid ${HANDLE_COLOR}` : 'none'),
                  outlineOffset: '-2px',
                  overflow: isSel ? 'visible' : 'hidden',
                  zIndex: isSel ? 5 : 1,
                  cursor: cellEditMode ? 'pointer' : 'default',
                  pointerEvents: cellEditMode ? 'auto' : 'none',
                  ...spanStyle,
                }}
                onClick={cellEditMode ? (e) => { e.stopPropagation(); setSelectedTC({ ri, ci }); } : undefined}
              >
                {tCell.isTitle && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1"
                    style={{ position: 'absolute', top: 2, right: 2, zIndex: 10, pointerEvents: 'none' }}>
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
                  </svg>
                )}
                {tCell.component ? renderTableCellComponent(tCell.component) : (tCell.content || '\u00A0')}
                {isSel && renderHandles(ri, ci)}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const CellContent = ({
  cell,
  isEditing,
  onContentChange,
  onImageUrlChange,
  isSelected,
  onCellInput,
  onLoopVarTrigger,
  onSelectCell,
  onUpdateCell,
  onUpdateCellComplete,
  selectedCellId,
  isReadOnly,
  tableCellHighlight,
  allCells,
}) => {
  const contentRef = useRef(null);
  const socialIconsRef = useRef(null);
  const lastSocialDimensionsRef = useRef({ width: 0, height: 0 });
  const prevBtnDimsRef = useRef({ w: null, h: null });
  const [imageUrlInput, setImageUrlInput] = useState(cell.imageUrl || '');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [localUrlError, setLocalUrlError] = useState(false);
  const isEditingRef = useRef(false);
  const hasInitializedCursor = useRef(false);
  const textValueRef = useRef(cell.content || '');

  const isValidUrl = (string) => {
    if (!string || string.trim() === '') return false;
    try {
      const url = new URL(string);
      return url.protocol === 'https:';
    } catch {
      return false;
    }
  };
  useEffect(() => {
    setImageUrlInput(cell.imageUrl || '');
  }, [cell.imageUrl]);

  const autoResizeTextCell = useCallback(() => {
    if (cell.type !== CELL_TYPES.TEXT || !contentRef.current) return;
    const element = contentRef.current;
    const hasContent = (element.textContent || '').trim().length > 0;
    const minHeight = 40;
    if (!hasContent) {
      if (cell.height > minHeight) onUpdateCell(cell.id, { height: minHeight });
      return;
    }
    const neededHeight = Math.max(minHeight, element.scrollHeight + 15);
    const heightDiff = neededHeight - cell.height;
    if (heightDiff < 0 || Math.abs(heightDiff) > 10) {
      onUpdateCell(cell.id, { height: Math.ceil(neededHeight) });
    }
  }, [cell.type, cell.height, cell.id, onUpdateCell]);

  useEffect(() => {
    if (cell.type === CELL_TYPES.SOCIAL) {
      const socialLinks = cell.socialLinks || {};
      const activeCount = Object.values(socialLinks).filter(Boolean).length;

      if (activeCount > 0) {
        const timeoutId = setTimeout(() => {
          if (!socialIconsRef.current) return;

          const iconContainer = socialIconsRef.current;
          const measuredWidth = iconContainer.scrollWidth;
          const measuredHeight = iconContainer.scrollHeight;

          const padding = 20;
          const neededWidth = measuredWidth + padding;
          const neededHeight = measuredHeight + padding;

          const lastDims = lastSocialDimensionsRef.current;
          const widthChanged = Math.abs(neededWidth - lastDims.width) > 5;
          const heightChanged = Math.abs(neededHeight - lastDims.height) > 5;

          if (widthChanged || heightChanged) {
            const updates = {};
            if (
              neededWidth > cell.width ||
              (neededWidth < cell.width && cell.width - neededWidth > 30)
            ) {
              updates.width = Math.ceil(neededWidth);
            }
            if (
              neededHeight > cell.height ||
              (neededHeight < cell.height && cell.height - neededHeight > 20)
            ) {
              updates.height = Math.ceil(neededHeight);
            }

            if (Object.keys(updates).length > 0) {
              lastSocialDimensionsRef.current = {
                width: updates.width || cell.width,
                height: updates.height || cell.height,
              };
              onUpdateCell(cell.id, updates);
            }
          }
        }, 100);

        return () => clearTimeout(timeoutId);
      } else {
        if (cell.width !== 250 || cell.height !== 80) {
          onUpdateCell(cell.id, { width: 250, height: 80 });
          lastSocialDimensionsRef.current = { width: 250, height: 80 };
        }
      }
    }
  }, [cell.type, cell.socialLinks, cell.iconSpacing, cell.id, onUpdateCell]);

  useEffect(() => {
    if (cell.type !== CELL_TYPES.BUTTON) return;
    const btnW = cell.buttonWidth != null && cell.buttonWidth !== '' ? Number(cell.buttonWidth) : 40;
    const btnH = cell.buttonHeight != null && cell.buttonHeight !== '' ? Number(cell.buttonHeight) : 15;
    const prev = prevBtnDimsRef.current;
    const dimsChanged = prev.w !== null && (prev.w !== btnW || prev.h !== btnH);
    prevBtnDimsRef.current = { w: btnW, h: btnH };
    const timeoutId = setTimeout(() => {
      const buttonEl = document.querySelector(`[data-cell-id="${cell.id}"] .cell-button-inner`);
      if (!buttonEl) return;
      const renderedW = buttonEl.scrollWidth;
      const renderedH = buttonEl.scrollHeight;
      const neededW = renderedW + 20;
      const neededH = renderedH + 10;
      const updates = {};
      if (dimsChanged) {
        if (Math.abs(cell.width - neededW) > 5) updates.width = Math.max(cell.minWidth || 50, Math.ceil(neededW));
        if (Math.abs(cell.height - neededH) > 5) updates.height = Math.max(cell.minHeight || 30, Math.ceil(neededH));
      } else {
        if (cell.width < neededW - 5) updates.width = Math.ceil(neededW);
        if (cell.height < neededH - 5) updates.height = Math.ceil(neededH);
      }
      if (renderedW > btnW && Math.abs(renderedW - btnW) > 3) updates.buttonWidth = Math.ceil(renderedW);
      if (renderedH > btnH && Math.abs(renderedH - btnH) > 3) updates.buttonHeight = Math.ceil(renderedH);
      if (Object.keys(updates).length > 0) {
        if (updates.buttonWidth) prevBtnDimsRef.current.w = updates.buttonWidth;
        if (updates.buttonHeight) prevBtnDimsRef.current.h = updates.buttonHeight;
        onUpdateCell(cell.id, updates);
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [cell.type, cell.buttonWidth, cell.buttonHeight, cell.content, cell.styles?.padding, cell.id, onUpdateCell]);

  useEffect(() => {
    const timeoutId = setTimeout(autoResizeTextCell, 50);
    return () => clearTimeout(timeoutId);
  }, [cell.content, isEditing, autoResizeTextCell]);

  const handleBlur = (e) => {
    const relatedTarget = e && e.relatedTarget;
    if (
      relatedTarget &&
      relatedTarget.closest &&
      relatedTarget.closest('.cell-link-input-overlay, .cell-link-input-popup')
    ) {
      return;
    }
    if (contentRef.current) {
      textValueRef.current = contentRef.current.innerHTML;
    }
    if (contentRef.current && onContentChange) {
      onContentChange(textValueRef.current);
    }
    isEditingRef.current = false;
    hasInitializedCursor.current = false;
  };

  const handleTextInput = (e) => {
    if (contentRef.current) {
      textValueRef.current = contentRef.current.innerHTML;
    }
    if (onCellInput) onCellInput(e, cell.id);
    setTimeout(autoResizeTextCell, 0);
  };

  useEffect(() => {
    if (isEditing && contentRef.current && !isEditingRef.current) {
      isEditingRef.current = true;
      hasInitializedCursor.current = false;
      textValueRef.current = cell.content || '';
    }
  }, [isEditing]);

  const handleImageUrlSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isValidUrl(imageUrlInput) && onImageUrlChange) {
      onImageUrlChange(imageUrlInput.trim());
      setShowUrlInput(false);
      onSelectCell(cell.id);
    }
  };

  const handleImageUrlKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleImageUrlSubmit(e);
    } else if (e.key === 'Escape') {
      setShowUrlInput(false);
      setImageUrlInput(cell.imageUrl || '');
    }
  };

  switch (cell.type) {
    case CELL_TYPES.TEXT:
      if (contentRef.current && !isEditing && !isEditingRef.current) {
        if (contentRef.current.innerHTML !== cell.content) {
          contentRef.current.innerHTML = cell.content || '';
        }
      }

      return (
        <div
          ref={contentRef}
          className='cell-text-content'
          contentEditable={isSelected && isEditing}
          onInput={handleTextInput}
          onPaste={handleTextInput}
          suppressContentEditableWarning
          onBlur={handleBlur}
          onFocus={() => {
            if (contentRef.current && !hasInitializedCursor.current && isEditing) {
              hasInitializedCursor.current = true;
              const selection = window.getSelection();
              if (selection) {
                if (selection.rangeCount === 0 || selection.isCollapsed) {
                  const range = document.createRange();
                  range.selectNodeContents(contentRef.current);
                  range.collapse(false);
                  selection.removeAllRanges();
                  selection.addRange(range);
                }
              }
            }
          }}
          onClick={(e) => {
            if (isEditing) {
              e.stopPropagation();
            }
          }}
          onMouseDown={(e) => {
            if (isEditing) {
              e.stopPropagation();
            }
          }}
          dangerouslySetInnerHTML={!isEditing ? { __html: cell.content || '' } : undefined}
          style={{
            minHeight: '100%',
            outline: 'none',
            fontSize: cell.styles?.fontSize || '16px',
            fontFamily: cell.styles?.fontFamily || 'Arial, sans-serif',
            color: cell.styles?.color || '#333333',
            textAlign: cell.styles?.textAlign || 'left',
            cursor: isSelected ? 'text' : 'move',
            overflow: 'visible',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          {isEditing && !contentRef.current?.innerHTML ? cell.content || '' : null}
        </div>
      );

    case CELL_TYPES.IMAGE:
      return (
        <div
          className='cell-image-content'
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {cell.imageUrl ? (
            <>
              {cell.redirectUrl && isValidUrl(cell.redirectUrl) ? (
                <a
                  href={cell.redirectUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ display: 'block', width: '100%', height: '100%' }}
                  onClick={(e) => {
                    if (!isReadOnly) {
                      e.preventDefault();
                    }
                  }}
                >
                  <img
                    src={cell.imageUrl}
                    alt={cell.imageAlt || 'Image'}
                    style={{
                      width: '100%',
                      height: isSelected ? 'calc(100% - 36px)' : '100%',
                      objectFit: cell.imageFit || 'fill',
                      display: cell.isImageBroken ? 'none' : 'block',
                    }}
                    onError={() => {
                      if (!cell.isImageBroken) {
                        onSelectCell(cell.id);
                        onUpdateCell(cell.id, { isImageBroken: true });
                      }
                    }}
                  />
                </a>
              ) : (
                <img
                  src={cell.imageUrl}
                  alt={cell.imageAlt || 'Image'}
                  style={{
                    width: '100%',
                    height: isSelected ? 'calc(100% - 36px)' : '100%',
                    objectFit: cell.imageFit || 'fill',
                    display: cell.isImageBroken ? 'none' : 'block',
                  }}
                  onError={() => {
                    if (!cell.isImageBroken) {
                      onSelectCell(cell.id);
                      onUpdateCell(cell.id, { isImageBroken: true });
                    }
                  }}
                />
              )}
              {cell.isImageBroken && (
                <div
                  style={{
                    width: '100%',
                    height: isSelected ? 'calc(100% - 36px)' : '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff1f0',
                    border: '1px solid #ffccc7',
                    color: '#ff4d4f',
                    fontSize: '12px',
                    textAlign: 'center',
                    padding: '10px',
                  }}
                >
                  Failed to load image
                </div>
              )}
              {isSelected && (
                <div
                  className='cell-image-url-edit'
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '4px',
                    background: 'rgba(0,0,0,0.7)',
                  }}
                >
                  <input
                    type='text'
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    onKeyDown={handleImageUrlKeyDown}
                    onBlur={handleImageUrlSubmit}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    placeholder='Enter image URL...'
                    className='cell-image-url-input'
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: `1px solid ${
                        imageUrlInput && !isValidUrl(imageUrlInput) ? '#ff4d4f' : '#29e67c'
                      }`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: '#fff',
                      outline: 'none',
                    }}
                  />
                  {imageUrlInput && !isValidUrl(imageUrlInput) && (
                    <div style={{ color: '#ff4d4f', fontSize: '10px', marginTop: '2px' }}>
                      Invalid URL
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              className='cell-image-placeholder'
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                border: '2px dashed #ddd',
                borderRadius: '4px',
                padding: '16px',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ marginBottom: '12px', color: '#666', fontSize: '13px' }}>
                Enter image URL below
              </div>
              <input
                type='text'
                value={imageUrlInput}
                onChange={(e) => setImageUrlInput(e.target.value)}
                onKeyDown={handleImageUrlKeyDown}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder='https://example.com/image.jpg'
                className='cell-image-url-input'
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '13px',
                  background: '#fff',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#29e67c';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ccc';
                  if (imageUrlInput.trim()) {
                    handleImageUrlSubmit(e);
                  }
                }}
              />
              {imageUrlInput && !isValidUrl(imageUrlInput) && (
                <div style={{ color: '#ff4d4f', fontSize: '11px', marginTop: '4px' }}>
                  Invalid URL
                </div>
              )}
              <button
                type='button'
                onClick={handleImageUrlSubmit}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={!isValidUrl(imageUrlInput)}
                style={{
                  marginTop: '8px',
                  padding: '6px 16px',
                  background: isValidUrl(imageUrlInput) ? '#29e67c' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: isValidUrl(imageUrlInput) ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                }}
              >
                Load Image
              </button>
            </div>
          )}
        </div>
      );

    case CELL_TYPES.BUTTON:
      const btnW = cell.buttonWidth != null && cell.buttonWidth !== '' ? `${cell.buttonWidth}px` : '40px';
      const btnH = cell.buttonHeight != null && cell.buttonHeight !== '' ? `${cell.buttonHeight}px` : '15px';
      return (
        <div
          className='cell-button-content'
          style={{
            display: 'flex',
            justifyContent:
              cell.styles?.textAlign === 'left'
                ? 'flex-start'
                : cell.styles?.textAlign === 'right'
                ? 'flex-end'
                : 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <a
            href={cell.buttonUrl || '#'}
            className='cell-button-inner'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: btnW,
              minHeight: btnH,
              padding: cell.styles?.padding || '12px 20px',
              backgroundColor: cell.styles?.backgroundColor || '#7a6a55',
              color: cell.styles?.color || '#ffffff',
              borderRadius: cell.styles?.borderRadius || '10px',
              border:
                cell.styles?.borderColor && cell.styles?.borderColor !== 'transparent'
                  ? `2px solid ${cell.styles?.borderColor}`
                  : 'none',
              textDecoration: 'none',
              fontWeight: cell.styles?.fontWeight || '600',
              boxSizing: 'border-box',
            }}
            onClick={(e) => e.preventDefault()}
          >
            {cell.content || 'Button'}
          </a>
        </div>
      );

    case CELL_TYPES.DIVIDER:
      return (
        <div className='cell-divider-content' style={{ padding: '8px 0' }}>
          <div
            className='cell-divider-line'
            style={{
              borderTop: `${cell.dividerThickness || 2}px solid ${cell.dividerColor || '#7a6a55'}`,
              width: '100%',
            }}
          />
        </div>
      );

    case CELL_TYPES.SOCIAL:
      const socialLinks = cell.socialLinks || {};
      const socialOrder = cell.socialOrder || [
        'linkedin',
        'facebook',
        'twitter',
        'instagram',
        'youtube',
      ];
      const activeLinks = socialOrder.filter((key) => socialLinks[key]);

      const SOCIAL_LABELS = {
        linkedin: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
        ),
        facebook: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
        ),
        twitter: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="#000000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        ),
        instagram: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
        ),
        youtube: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 4v16l13-8z"/></svg>
        ),
      };

      return (
        <div
          className='cell-social-content'
          style={{
            textAlign: cell.styles?.textAlign || 'center',
            padding: '8px 0',
            display: 'flex',
            justifyContent:
              cell.styles?.textAlign === 'left'
                ? 'flex-start'
                : cell.styles?.textAlign === 'right'
                ? 'flex-end'
                : 'center',
            width: '100%',
          }}
        >
          {activeLinks.length === 0 ? (
            <div
              className='cell-social-placeholder'
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: '#828282',
                fontSize: '13px',
                width: '100%',
                height: '100%',
              }}
            >
              <svg
                width='34'
                height='34'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <rect
                  x='3'
                  y='3'
                  width='18'
                  height='18'
                  rx='4'
                  stroke='#03e67c'
                  strokeWidth='1.5'
                />
                <path
                  d='M15 14H13V21H10V14H8V11H10V9.5C10 7.567 11.567 6 13.5 6H16V9H14.5C13.672 9 13 9.672 13 10.5V11H16L15 14Z'
                  stroke='#03e67c'
                  strokeWidth='1.5'
                  fill='none'
                />
              </svg>
              <div style={{ font: 'normal normal normal 13px/18px Roboto' }}>
                Add Handles from the menu
              </div>
            </div>
          ) : (
            <div
              ref={socialIconsRef}
              className='cell-social-icons'
              style={{
                display: 'inline-flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: cell.iconSpacing || '10px',
                width: 'auto',
                maxWidth: '100%',
              }}
            >
              {activeLinks.map((key) => (
                <a
                  key={key}
                  href={socialLinks[key]}
                  className='cell-social-icon'
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: cell.iconColor || '#EED191',
                    color: '#fff',
                    textDecoration: 'none',
                    margin: `0 ${cell.iconSpacing || 10}px`,
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                  onClick={(e) => e.preventDefault()}
                  title={key}
                >
                  {SOCIAL_LABELS[key]}
                </a>
              ))}
            </div>
          )}
        </div>
      );

    case CELL_TYPES.SPACER:
      return <div className='cell-spacer-content' style={{ height: '100%' }} />;

    case CELL_TYPES.HTML:
      return (
        <div
          className='cell-html-content'
          dangerouslySetInnerHTML={{ __html: cell.content || '' }}
        />
      );

    case CELL_TYPES.LOOP:
      const loopConds = cell.loopConditions || [];
      return (
        <div
          className='cell-loop-content'
          style={{
            display: 'flex', flexDirection: 'column', gap: 6,
            padding: '6px 8px', width: '100%', height: '100%', boxSizing: 'border-box',
          }}
        >
          {loopConds.length === 0 && (
            <div style={{ color: '#999', fontSize: 12, fontStyle: 'italic' }}>Select conditions from panel →</div>
          )}
          {loopConds.map((cond, idx) => {
            // Render {{end}} on separate line, opening conditions inline
            const isEnd = cond.type === 'end';
            return (
              <div
                key={cond.id || idx}
                style={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  width: isEnd ? '100%' : 'auto',
                }}
              >
                <div
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 2,
                    padding: '3px 8px', borderRadius: 4,
                    background: isEnd ? '#fef2f2' : '#f5f3ff',
                    border: `1px solid ${isEnd ? '#fecaca' : '#e9d5ff'}`,
                    fontSize: 12, fontFamily: 'monospace',
                  }}
                >
                  {isEnd ? (
                    <span style={{ color: '#dc2626', fontWeight: 600 }}>{'{{end}}'}</span>
                  ) : (
                    <>
                      <span style={{ color: '#6c63ff', fontWeight: 600 }}>{`{{${cond.type} .`}</span>
                      <input
                        type='text'
                        value={cond.value || ''}
                        placeholder='variable'
                        onChange={(e) => {
                          let newVal = e.target.value;
                          // Detect [ trigger for variable dropdown
                          if (newVal.includes('[')) {
                            newVal = newVal.replace(/\[/g, '');
                            const updated = [...loopConds];
                            updated[idx] = { ...updated[idx], value: newVal };
                            onUpdateCell(cell.id, { loopConditions: updated });
                            if (onLoopVarTrigger) onLoopVarTrigger(cell.id, idx, e.target);
                            return;
                          }
                          // Strip ] if typed
                          if (newVal.includes(']')) {
                            newVal = newVal.replace(/\]/g, '');
                          }
                          const updated = [...loopConds];
                          updated[idx] = { ...updated[idx], value: newVal };
                          onUpdateCell(cell.id, { loopConditions: updated });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{
                          border: 'none', outline: 'none', background: 'transparent',
                          fontSize: 12, fontFamily: 'monospace', color: '#333',
                          width: Math.max(60, (cond.value || '').length * 8 + 20),
                          padding: '1px 2px',
                        }}
                      />
                      <span style={{ color: '#6c63ff', fontWeight: 600 }}>{'}}'}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );

    case CELL_TYPES.TABLE:
      return (
        <TableCellRenderer
          cell={cell}
          onUpdateCell={onUpdateCell}
          onUpdateCellComplete={onUpdateCellComplete}
          tableCellHighlight={tableCellHighlight}
          allCells={allCells}
        />
      );

    default:
      return <div className='cell-unknown-content'>Unknown cell type</div>;
  }
};

const CellCanvas = ({
  schema,
  selectedCellId,
  onSelectCell,
  onUpdateCell,
  onUpdateCellComplete,
  onContentChange,
  isReadOnly = false,
  onCellInput,
  onLoopVarTrigger,
}) => {
  const canvasRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [editingCellId, setEditingCellId] = useState(null);
  const [tableCellHighlight, setTableCellHighlight] = useState(null);
  const highlightRef = useRef(null);

  const { cells = [], canvasWidth = 600, canvasHeight = 800, bgColor = '#ffffff' } = schema;

  const cellsRef = useRef(cells);
  cellsRef.current = cells;

  const scrollRAF = useRef(null);
  const lastMouseY = useRef(null);
  const lastMouseX = useRef(null);

  const sortedCells = sortCellsByZIndex(cells);

  const flushEditingContent = useCallback(() => {
    if (!editingCellId) return;
    const cellElement = canvasRef.current?.querySelector(
      `[data-cell-id="${editingCellId}"] .cell-text-content`,
    );
    if (cellElement) {
      onContentChange(editingCellId, cellElement.innerHTML);
    }
  }, [editingCellId, onContentChange]);

  const handleCellClick = useCallback(
    (e, cellId) => {
      e.stopPropagation();
      if (isReadOnly) return;
      setEditingCellId(null);
      onSelectCell(cellId);
    },
    [onSelectCell, isReadOnly],
  );

  const handleCellDoubleClick = useCallback(
    (e, cell) => {
      e.stopPropagation();
      if (isReadOnly) return;
      if (cell.type === CELL_TYPES.TEXT) {
        if (editingCellId === cell.id) return;

        flushEditingContent();
        setEditingCellId(cell.id);
        setTimeout(() => {
          const cellElement = canvasRef.current?.querySelector(
            `[data-cell-id="${cell.id}"] .cell-text-content`,
          );
          if (cellElement) {
            const content = (cell.content || '').trim();
            const plainText = content.replace(/<[^>]*>?/gm, '').trim();
            const isPlaceholder = !plainText || plainText === 'Double Click to Enter Text...';

            if (isPlaceholder) {
              cellElement.innerHTML = '';
            } else {
              cellElement.innerHTML = cell.content;
            }

            cellElement.focus();
            const selection = window.getSelection();
            const range = document.createRange();

            range.selectNodeContents(cellElement);
            range.collapse(false);

            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 0);
      }
    },
    [isReadOnly, editingCellId, flushEditingContent],
  );

  const handleCanvasClick = useCallback(
    (e) => {
      if (e.target === canvasRef.current) {
        flushEditingContent();
        onSelectCell(null);
        setEditingCellId(null);
      }
    },
    [onSelectCell, flushEditingContent],
  );

  const handleDragStart = useCallback(
    (e, cell) => {
      if (isReadOnly || editingCellId === cell.id) return;
      e.preventDefault();
      e.stopPropagation();
      flushEditingContent();
      setEditingCellId(null);
      onSelectCell(cell.id);

      const rect = canvasRef.current.getBoundingClientRect();
      const scrollContainer = canvasRef.current?.closest('.cell-canvas-scroll-container');
      setDragging({
        cellId: cell.id,
        startX: e.clientX,
        startY: e.clientY,
        cellStartX: cell.x,
        cellStartY: cell.y,
        canvasRect: rect,
        initialScrollTop: scrollContainer?.scrollTop || 0,
      });
    },
    [isReadOnly, editingCellId, onSelectCell, flushEditingContent],
  );

  const handleResizeStart = useCallback(
    (e, cell, handle) => {
      if (isReadOnly) return;
      e.preventDefault();
      e.stopPropagation();

      const rect = canvasRef.current.getBoundingClientRect();
      // For TABLE cells, compute min dims ONCE at the start so they stay fixed during drag
      let tableMinW = 80, tableMinH = 40;
      if (cell.type === CELL_TYPES.TABLE) {
        const dims = getTableMinDims(cell, cells);
        tableMinW = dims.minTableW;
        tableMinH = dims.minTableH;
      }
      setResizing({
        cellId: cell.id,
        handle: handle.position,
        startX: e.clientX,
        startY: e.clientY,
        cellStartX: cell.x,
        cellStartY: cell.y,
        cellStartWidth: cell.width,
        cellStartHeight: cell.height,
        canvasRect: rect,
        tableMinW,
        tableMinH,
      });
    },
    [isReadOnly, cells],
  );

  useEffect(() => {
    if (!dragging && !resizing) return;

    const SCROLL_EDGE = 60;
    const SCROLL_SPEED = 12;

    const getScrollContainer = () =>
      canvasRef.current?.closest('.cell-canvas-scroll-container');

    const updateDraggedCellPosition = () => {
      if (!dragging || lastMouseX.current === null || lastMouseY.current === null) return;
      const cell = cellsRef.current.find((c) => c.id === dragging.cellId);
      if (!cell) return;

      const container = getScrollContainer();
      const scrollDelta = (container?.scrollTop || 0) - dragging.initialScrollTop;

      const deltaX = lastMouseX.current - dragging.startX;
      const deltaY = lastMouseY.current - dragging.startY;

      let newX = dragging.cellStartX + deltaX;
      let newY = dragging.cellStartY + deltaY + scrollDelta;

      const actualCanvasWidth = canvasRef.current?.offsetWidth || canvasWidth;
      newX = Math.round(Math.max(0, Math.min(newX, actualCanvasWidth - cell.width)));
      newY = Math.round(Math.max(0, newY));

      onUpdateCell(dragging.cellId, { x: newX, y: newY });
    };

    const doAutoScroll = () => {
      const container = getScrollContainer();
      if (!container || lastMouseY.current === null) {
        scrollRAF.current = null;
        return;
      }
      const rect = container.getBoundingClientRect();
      const mouseY = lastMouseY.current;
      let scrolled = false;

      if (mouseY < rect.top + SCROLL_EDGE) {
        const intensity = 1 - (mouseY - rect.top) / SCROLL_EDGE;
        container.scrollTop -= Math.ceil(SCROLL_SPEED * Math.max(0, intensity));
        scrolled = true;
      } else if (mouseY > rect.bottom - SCROLL_EDGE) {
        const intensity = 1 - (rect.bottom - mouseY) / SCROLL_EDGE;
        container.scrollTop += Math.ceil(SCROLL_SPEED * Math.max(0, intensity));
        scrolled = true;
      }

      if (scrolled) {
        updateDraggedCellPosition();
        scrollRAF.current = requestAnimationFrame(doAutoScroll);
      } else {
        scrollRAF.current = null;
      }
    };

    const handleMouseMove = (e) => {
      lastMouseX.current = e.clientX;
      lastMouseY.current = e.clientY;

      if (!scrollRAF.current) {
        const container = getScrollContainer();
        if (container) {
          const rect = container.getBoundingClientRect();
          if (e.clientY < rect.top + SCROLL_EDGE || e.clientY > rect.bottom - SCROLL_EDGE) {
            scrollRAF.current = requestAnimationFrame(doAutoScroll);
          }
        }
      }

      if (dragging) {
        const container = getScrollContainer();
        const scrollDelta = (container?.scrollTop || 0) - dragging.initialScrollTop;

        const deltaX = e.clientX - dragging.startX;
        const deltaY = e.clientY - dragging.startY;

        const cell = cells.find((c) => c.id === dragging.cellId);
        if (!cell) return;

        let newX = dragging.cellStartX + deltaX;
        let newY = dragging.cellStartY + deltaY + scrollDelta;

        const actualCanvasWidth = canvasRef.current?.offsetWidth || canvasWidth;
        newX = Math.round(Math.max(0, Math.min(newX, actualCanvasWidth - cell.width)));
        newY = Math.round(Math.max(0, newY));

        onUpdateCell(dragging.cellId, { x: newX, y: newY });

        // Compute table cell highlight for the flying component
        if (cell.type !== CELL_TYPES.TABLE) {
          let found = null;
          for (const tc of cellsRef.current) {
            if (tc.type !== CELL_TYPES.TABLE || !tc.tableModel) continue;
            const tm = tc.tableModel;
            const cx = newX + cell.width / 2, cy = newY + cell.height / 2;
            if (cx < tc.x || cx > tc.x + tc.width || cy < tc.y || cy > tc.y + tc.height) continue;
            const relCx = cx - tc.x, relCy = cy - tc.y;
            const nR = tm.grid.length, nC = tm.grid[0]?.length || 1;
            const cwArr = (tm.colWidths || []).map((v) => (v != null ? v : 100 / nC));
            const rhArr = (tm.rowHeights || []).map((v) => (v != null ? v : 100 / nR));
            const cPx = [0]; for (let k = 0; k < nC; k++) cPx.push(cPx[k] + (cwArr[k] / 100) * tc.width);
            const rPx = [0]; for (let k = 0; k < nR; k++) rPx.push(rPx[k] + (rhArr[k] / 100) * tc.height);
            let ci = nC - 1; for (let k = 0; k < nC; k++) { if (relCx < cPx[k + 1]) { ci = k; break; } }
            let ri = nR - 1; for (let k = 0; k < nR; k++) { if (relCy < rPx[k + 1]) { ri = k; break; } }
            found = { tableCellId: tc.id, ri, ci };
            break;
          }
          const prev = highlightRef.current;
          if (found && (!prev || prev.tableCellId !== found.tableCellId || prev.ri !== found.ri || prev.ci !== found.ci)) {
            highlightRef.current = found;
            setTableCellHighlight(found);
          } else if (!found && prev) {
            highlightRef.current = null;
            setTableCellHighlight(null);
          }
        }
      }

      if (resizing) {
        const deltaX = e.clientX - resizing.startX;
        const deltaY = e.clientY - resizing.startY;

        const cell = cellsRef.current.find((c) => c.id === resizing.cellId);
        if (!cell) return;

        let newX = resizing.cellStartX;
        let newY = resizing.cellStartY;
        let newWidth = resizing.cellStartWidth;
        let newHeight = resizing.cellStartHeight;

        const handle = resizing.handle;

        if (handle.includes('n')) {
          newY = resizing.cellStartY + deltaY;
          newHeight = resizing.cellStartHeight - deltaY;
        }
        if (handle.includes('s')) {
          newHeight = resizing.cellStartHeight + deltaY;
        }
        if (handle.includes('w')) {
          newX = resizing.cellStartX + deltaX;
          newWidth = resizing.cellStartWidth - deltaX;
        }
        if (handle.includes('e')) {
          newWidth = resizing.cellStartWidth + deltaX;
        }

        let minHeight = cell.minHeight || 30;
        let minWidth = cell.minWidth || 50;

        if (cell.type === CELL_TYPES.TEXT) {
          const cellElement = canvasRef.current?.querySelector(
            `[data-cell-id="${cell.id}"] .cell-text-content`,
          );
          if (cellElement) {
            const hasContent = (cellElement.textContent || '').trim().length > 0;
            minHeight = hasContent ? Math.max(40, cellElement.scrollHeight + 15) : 40;
          }
        } else if (cell.type === CELL_TYPES.BUTTON) {
          const buttonElement = canvasRef.current?.querySelector(
            `[data-cell-id="${cell.id}"] .cell-button-inner`,
          );
          if (buttonElement) {
            minHeight = Math.max(40, buttonElement.offsetHeight + 10);
            minWidth = Math.max(80, buttonElement.offsetWidth + 20);
          }
        } else if (cell.type === CELL_TYPES.SOCIAL) {
          const socialElement =
            canvasRef.current?.querySelector(`[data-cell-id="${cell.id}"] .cell-social-icons`) ||
            canvasRef.current?.querySelector(
              `[data-cell-id="${cell.id}"] .cell-social-placeholder`,
            );
          if (socialElement) {
            const isPlaceholder = socialElement.classList.contains('cell-social-placeholder');
            minHeight = isPlaceholder ? 80 : Math.max(50, socialElement.offsetHeight + 20);
            minWidth = isPlaceholder ? 150 : Math.max(100, socialElement.offsetWidth + 20);
          }
        } else if (cell.type === CELL_TYPES.TABLE) {
          minWidth = resizing.tableMinW || 80;
          minHeight = resizing.tableMinH || 40;
        }
        newWidth = Math.max(minWidth, newWidth);
        newHeight = Math.max(minHeight, newHeight);
        // For shrink-from-top/left: re-derive position so table doesn't jump
        if (handle.includes('n')) { newY = resizing.cellStartY + (resizing.cellStartHeight - newHeight); }
        if (handle.includes('w')) { newX = resizing.cellStartX + (resizing.cellStartWidth - newWidth); }

        const actualCanvasWidth = canvasRef.current?.offsetWidth || canvasWidth;

        newX = Math.round(Math.max(0, newX));
        newY = Math.round(Math.max(0, newY));
        newWidth = Math.round(Math.min(newWidth, actualCanvasWidth - newX));
        newHeight = Math.round(newHeight);
        onUpdateCell(resizing.cellId, {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        });
      }
    };

    const handleMouseUp = () => {
      const latestCells = cellsRef.current;
      const actualCanvasWidth = canvasRef.current?.offsetWidth || canvasWidth;
      if (dragging) {
        const cell = latestCells.find((c) => c.id === dragging.cellId);
        if (cell) {
          const otherCells = latestCells.filter((c) => c.id !== dragging.cellId);
          const adjustedPos = findNonOverlappingPosition(cell, otherCells, actualCanvasWidth);
          onUpdateCell(dragging.cellId, { x: adjustedPos.x, y: adjustedPos.y });
          if (onUpdateCellComplete) {
            onUpdateCellComplete(dragging.cellId, { x: adjustedPos.x, y: adjustedPos.y });
          }
        }
      }
      if (resizing) {
        const cell = latestCells.find((c) => c.id === resizing.cellId);
        if (cell) {
          const otherCells = latestCells.filter((c) => c.id !== resizing.cellId);
          const adjustedPos = findNonOverlappingPosition(cell, otherCells, actualCanvasWidth);
          onUpdateCell(resizing.cellId, { x: adjustedPos.x, y: adjustedPos.y, width: cell.width, height: cell.height });
          if (onUpdateCellComplete) {
            onUpdateCellComplete(resizing.cellId, {
              x: adjustedPos.x,
              y: adjustedPos.y,
              width: cell.width,
              height: cell.height,
            });
          }
        }
      }
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
        scrollRAF.current = null;
      }
      lastMouseX.current = null;
      lastMouseY.current = null;
      highlightRef.current = null;
      setTableCellHighlight(null);
      setDragging(null);
      setResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
        scrollRAF.current = null;
      }
    };
  }, [dragging, resizing, cells, canvasWidth, canvasHeight, onUpdateCell, onUpdateCellComplete]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editingCellId) {
        if (e.key === 'Escape') {
          setEditingCellId(null);
        }
        return;
      }

      if (!selectedCellId) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
      } else if (e.key === 'Escape') {
        onSelectCell(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCellId, editingCellId, onSelectCell]);

  const handleContentChange = useCallback(
    (cellId, newContent) => {
      onContentChange(cellId, newContent);
      setEditingCellId(null);
    },
    [onContentChange],
  );

  const handleImageUrlChange = useCallback(
    (cellId, imageUrl) => {
      onUpdateCell(cellId, { imageUrl, isImageBroken: false });
    },
    [onUpdateCell],
  );

  const dynamicHeight = useMemo(() => {
    if (!cells || cells.length === 0) return canvasHeight;
    const maxBottom = Math.max(...cells.map((c) => (c.y || 0) + (c.height || 0)));
    return Math.max(canvasHeight, maxBottom + 50);
  }, [cells, canvasHeight]);

  return (
    <table
      cellPadding='0'
      cellSpacing='0'
      border='0'
      className='cell-canvas-table'
      style={{
        width: '100%',
        margin: '0',
        borderCollapse: 'collapse',
        backgroundColor: bgColor,
        border: 'none',
      }}
    >
      <tbody>
        <tr>
          <td
            ref={canvasRef}
            className='cell-canvas'
            style={{
              position: 'relative',
              width: '100%',
              minHeight: `${dynamicHeight}px`,
              height: `${dynamicHeight}px`,
              padding: 0,
              border: 'none',
              verticalAlign: 'top',
              backgroundColor: schema.bgColor || '#ffffff',
            }}
            onClick={handleCanvasClick}
            onDragOver={(e) => e.preventDefault()}
          >
            {sortedCells.map((cell) => (
              <RenderedCell
                key={cell.id}
                cell={cell}
                selectedCellId={selectedCellId}
                editingCellId={editingCellId}
                draggingCellId={dragging?.cellId}
                resizingCellId={resizing?.cellId}
                onSelectCell={onSelectCell}
                onUpdateCell={onUpdateCell}
                onUpdateCellComplete={onUpdateCellComplete}
                onContentChange={handleContentChange}
                onImageUrlChange={handleImageUrlChange}
                onCellInput={onCellInput}
                onLoopVarTrigger={onLoopVarTrigger}
                isReadOnly={isReadOnly}
                onDragStart={handleDragStart}
                onResizeStart={handleResizeStart}
                onDoubleClick={(e, cell) => handleCellDoubleClick(e, cell)}
                tableCellHighlight={tableCellHighlight}
                allCells={cells}
              />
            ))}

            {cells.length === 0 && (
              <div className='cell-canvas-empty'>
                <p>Click "+ Cell" in the Component to add a cell</p>
                <p>Or any components from the right panel</p>
              </div>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default CellCanvas;
